import { useState } from "react";
import { Plus, X } from "lucide-react";
import api from "../lib/api";

const TYPES = {
    experiences: {
        label: "Expérience",
        endpoint: "/experiences",
        slugFrom: "title",
        defaults: () => ({
            title: "", slug: "", category: "culture", tagline: "", short_description: "",
            description: "", image: "/api/static/img/placeholder.jpg",
            city_slug: "le-treport", duration: "demi-journée",
            group_size: "groupe limité", price_from: "Sur demande",
            included: [], highlights: [], mood_tags: [], season: "",
            featured: false, position: 50,
            booking_url: "", booking_phone: "", booking_label: "",
        }),
        fields: [
            { k: "title", label: "Titre *", type: "text", required: true },
            { k: "category", label: "Catégorie *", type: "select", options: ["culture", "gastronomie", "nature", "artisan", "sport", "patrimoine", "bien-etre", "spiritueux", "gourmandise"] },
            { k: "city_slug", label: "Commune", type: "text", placeholder: "ex: le-treport, mers-les-bains, eu, cayeux-sur-mer" },
            { k: "tagline", label: "Tagline (1 ligne)", type: "text" },
            { k: "short_description", label: "Description courte (max 200 car.)", type: "textarea", rows: 3 },
            { k: "description", label: "Description longue *", type: "textarea", rows: 6, required: true },
            { k: "image", label: "URL image (ou /api/static/img/...)", type: "text" },
            { k: "duration", label: "Durée (ex: 2h, demi-journée)", type: "text" },
            { k: "group_size", label: "Taille groupe", type: "text" },
            { k: "price_from", label: "Tarif", type: "text" },
            { k: "booking_url", label: "URL de réservation (officielle)", type: "url" },
            { k: "booking_phone", label: "Téléphone réservation", type: "text" },
            { k: "booking_label", label: "Label CTA (ex: Réserver, Contacter)", type: "text" },
            { k: "featured", label: "Mettre en vedette", type: "checkbox" },
        ],
    },
    parcours: {
        label: "Parcours",
        endpoint: "/parcours",
        slugFrom: "title",
        defaults: () => ({
            title: "", slug: "", theme: "Patrimoine", tagline: "", short_description: "",
            description: "", image: "/api/static/img/placeholder.jpg",
            duration: "une journée", total_distance_km: 30,
            stops: [], best_season: "avril à octobre",
            mood_tags: [], featured: false, position: 50,
        }),
        fields: [
            { k: "title", label: "Titre *", type: "text", required: true },
            { k: "theme", label: "Thème", type: "text", placeholder: "ex: Gastronomie, Architecture, Nature" },
            { k: "tagline", label: "Tagline", type: "text" },
            { k: "short_description", label: "Description courte (max 250 car.)", type: "textarea", rows: 3 },
            { k: "description", label: "Description longue *", type: "textarea", rows: 6, required: true },
            { k: "image", label: "URL image", type: "text" },
            { k: "duration", label: "Durée", type: "text" },
            { k: "total_distance_km", label: "Distance totale (km)", type: "number" },
            { k: "best_season", label: "Saison conseillée", type: "text" },
            { k: "featured", label: "Mettre en vedette", type: "checkbox" },
        ],
    },
    events: {
        label: "Événement",
        endpoint: "/events",
        slugFrom: "title",
        defaults: () => ({
            title: "", slug: "", category: "concert", city_slug: "le-treport",
            short_description: "", description: "", image: "/api/static/img/placeholder.jpg",
            start_date: new Date().toISOString().slice(0, 10), end_date: "",
            location_name: "", lat: null, lng: null, address: "",
            price: "", tags: [], website: "", featured: false, verified: true,
        }),
        fields: [
            { k: "title", label: "Titre *", type: "text", required: true },
            { k: "category", label: "Catégorie *", type: "select", options: ["concert", "brocante", "marche-createurs", "expo", "food", "fete", "retraite-bien-etre", "soiree", "saisonnier"] },
            { k: "city_slug", label: "Commune (slug)", type: "text", placeholder: "ex: eu, le-treport, mers-les-bains" },
            { k: "start_date", label: "Date de début (YYYY-MM-DD) *", type: "date", required: true },
            { k: "end_date", label: "Date de fin (facultatif)", type: "date" },
            { k: "location_name", label: "Lieu (nom) *", type: "text", required: true },
            { k: "address", label: "Adresse complète", type: "text" },
            { k: "short_description", label: "Description courte", type: "textarea", rows: 2 },
            { k: "description", label: "Description longue *", type: "textarea", rows: 5, required: true },
            { k: "image", label: "URL image", type: "text" },
            { k: "price", label: "Tarif", type: "text" },
            { k: "website", label: "Site web officiel", type: "url" },
            { k: "featured", label: "Mettre en vedette", type: "checkbox" },
        ],
    },
};


function slugify(text) {
    return (text || "")
        .toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 80);
}


export default function AdminContentCreator() {
    const [open, setOpen] = useState(false);
    const [type, setType] = useState("experiences");
    const [form, setForm] = useState(TYPES.experiences.defaults());
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);

    const switchType = (newType) => {
        setType(newType);
        setForm(TYPES[newType].defaults());
        setSuccess(null); setError(null);
    };

    const set = (k, v) => setForm({ ...form, [k]: v });

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true); setError(null); setSuccess(null);
        try {
            const cfg = TYPES[type];
            const payload = { ...form };
            payload.slug = payload.slug || slugify(payload[cfg.slugFrom]);
            // Ensure short_description has a fallback so backend doesn't 422
            if (!payload.short_description && payload.description) {
                payload.short_description = payload.description.slice(0, 200);
            }
            // Number coercion
            if (type === "parcours" && typeof payload.total_distance_km === "string") {
                payload.total_distance_km = parseFloat(payload.total_distance_km) || null;
            }
            // Strip empty optional URLs that fail Pydantic HttpUrl validation when blank
            ["booking_url", "website"].forEach(k => { if (payload[k] === "") delete payload[k]; });
            const res = await api.post(cfg.endpoint, payload);
            setSuccess(`${cfg.label} créé : "${res.data.title || res.data.slug}"`);
            setForm(cfg.defaults());
        } catch (err) {
            const detail = err?.response?.data?.detail || err.message;
            setError(typeof detail === "string" ? detail : JSON.stringify(detail).slice(0, 300));
        } finally {
            setSaving(false);
        }
    };

    if (!open) {
        return (
            <section className="bg-cp-foret/95 text-cp-ivoire border border-cp-foret p-8 lg:col-span-2" data-testid="admin-content-creator-collapsed">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <p className="cp-kicker text-cp-sable mb-2">Création de contenu</p>
                        <p className="cp-serif text-2xl">Ajouter manuellement <em className="cp-display-italic text-cp-sable">une expérience, un parcours ou un événement</em>.</p>
                    </div>
                    <button
                        onClick={() => setOpen(true)}
                        data-testid="admin-creator-open"
                        className="cp-tiny text-cp-encre bg-cp-sable px-5 py-2.5 hover:bg-cp-ivoire transition-colors flex items-center gap-2"
                    >
                        <Plus size={14} strokeWidth={2} /> Nouvelle entrée
                    </button>
                </div>
            </section>
        );
    }

    const cfg = TYPES[type];
    return (
        <section className="bg-cp-ivoire border border-cp-foret border-2 p-8 lg:col-span-2" data-testid="admin-content-creator">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    {Object.entries(TYPES).map(([k, v]) => (
                        <button
                            key={k}
                            onClick={() => switchType(k)}
                            data-testid={`admin-creator-tab-${k}`}
                            className={`cp-tiny px-3 py-1.5 transition-colors ${
                                type === k ? "bg-cp-foret text-cp-ivoire" : "bg-cp-craie text-cp-gris hover:text-cp-noir"
                            }`}
                        >
                            {v.label}
                        </button>
                    ))}
                </div>
                <button
                    onClick={() => setOpen(false)}
                    data-testid="admin-creator-close"
                    className="text-cp-gris hover:text-cp-noir"
                >
                    <X size={18} />
                </button>
            </div>

            {success && <div className="bg-cp-foret/10 text-cp-foret p-3 mb-4 cp-tiny" data-testid="admin-creator-success">✓ {success}</div>}
            {error && <div className="bg-red-50 text-red-700 p-3 mb-4 cp-tiny" data-testid="admin-creator-error">✕ {error}</div>}

            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {cfg.fields.map(f => (
                    <Field key={f.k} field={f} value={form[f.k]} onChange={(v) => set(f.k, v)} fullwidth={f.type === "textarea"} />
                ))}
                <div className="md:col-span-2 flex items-center justify-between border-t border-cp-border pt-4 mt-2">
                    <p className="cp-tiny text-cp-gris">
                        Le slug est auto-généré depuis le titre. Tous les champs vérifiés réels avant publication.
                    </p>
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="admin-creator-submit"
                        className="cp-tiny text-cp-ivoire bg-cp-foret px-6 py-2.5 hover:bg-cp-noir transition-colors disabled:opacity-50"
                    >
                        {saving ? "Création…" : `Créer ${cfg.label.toLowerCase()}`}
                    </button>
                </div>
            </form>
        </section>
    );
}


function Field({ field, value, onChange, fullwidth }) {
    const cls = `w-full bg-cp-craie border border-cp-border px-3 py-2 cp-serif text-base focus:outline-none focus:border-cp-foret transition-colors`;
    return (
        <label className={`block ${fullwidth ? "md:col-span-2" : ""}`} data-testid={`admin-creator-field-${field.k}`}>
            <span className="cp-tiny text-cp-gris mb-1.5 block">{field.label}</span>
            {field.type === "textarea" ? (
                <textarea rows={field.rows || 3} value={value || ""} onChange={e => onChange(e.target.value)} className={`${cls} resize-y`} required={field.required} />
            ) : field.type === "select" ? (
                <select value={value || ""} onChange={e => onChange(e.target.value)} className={cls}>
                    {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
            ) : field.type === "checkbox" ? (
                <div className="flex items-center pt-1">
                    <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} className="accent-cp-foret w-4 h-4 mr-2" />
                    <span className="cp-tiny text-cp-noir">{value ? "Oui" : "Non"}</span>
                </div>
            ) : (
                <input type={field.type} value={value ?? ""} onChange={e => onChange(field.type === "number" ? e.target.value : e.target.value)} placeholder={field.placeholder || ""} className={cls} required={field.required} />
            )}
        </label>
    );
}
